import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  IconButton,
} from '@mui/material'
import {
  Close as CloseIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material'
import { useProviderActions } from '../../hooks/useProviderActions'
import { formatNumber, formatDate } from '../../utils/formatters'

const PriceSyncDialog = ({ open, provider, onClose, onSuccess }) => {
  const [syncStatus, setSyncStatus] = useState('idle') // idle, syncing, success, error
  const [syncResults, setSyncResults] = useState(null)
  const [error, setError] = useState(null)

  const { syncPrices, isLoading } = useProviderActions()

  useEffect(() => {
    if (!open) {
      setSyncStatus('idle')
      setSyncResults(null)
      setError(null)
    }
  }, [open])

  const handleSync = async () => {
    try {
      setSyncStatus('syncing')
      setError(null)
      
      const result = await syncPrices(provider?.id)
      
      setSyncResults(result)
      setSyncStatus('success')
      
      // Auto close after successful sync
      setTimeout(() => {
        onSuccess()
      }, 2000)
      
    } catch (err) {
      console.error('Sync error:', err)
      setError(err.message || 'Senkronizasyon sırasında hata oluştu')
      setSyncStatus('error')
    }
  }

  const getSyncStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <SyncIcon color="primary" className="rotate-animation" />
      case 'success':
        return <CheckCircleIcon color="success" />
      case 'error':
        return <ErrorIcon color="error" />
      default:
        return <SyncIcon color="action" />
    }
  }

  const getSyncStatusColor = () => {
    switch (syncStatus) {
      case 'syncing':
        return 'primary'
      case 'success':
        return 'success'
      case 'error':
        return 'error'
      default:
        return 'default'
    }
  }

  if (!provider) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getSyncStatusIcon()}
            <Typography variant="h6">
              Fiyat Senkronizasyonu - {provider.name}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={syncStatus === 'syncing'}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {syncStatus === 'idle' && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{provider.name}</strong> provider'ı için fiyat senkronizasyonu başlatılacak.
                Bu işlem birkaç dakika sürebilir.
              </Typography>
            </Alert>

            <Typography variant="h6" sx={{ mb: 2 }}>
              Mevcut Durum
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Mevcut Fiyat Sayısı"
                  secondary={`${formatNumber(provider.price_count || 0)} fiyat`}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <TrendingUpIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Toplam Kiralama"
                  secondary={`${formatNumber(provider.rental_count || 0)} kiralama`}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <InfoIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Son Güncelleme"
                  secondary={formatDate(provider.updated_at)}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary">
              Senkronizasyon işlemi sırasında:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Tüm ülke-servis kombinasyonları kontrol edilecek
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Güncel fiyatlar API'den çekilecek
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Veritabanı güncellenecek
              </Typography>
            </Box>
          </Box>
        )}

        {syncStatus === 'syncing' && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Fiyatlar Senkronize Ediliyor...
              </Typography>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Lütfen bekleyin, bu işlem birkaç dakika sürebilir.
              </Typography>
            </Box>

            <Alert severity="warning">
              Bu işlem sırasında pencereyi kapatmayın.
            </Alert>
          </Box>
        )}

        {syncStatus === 'success' && syncResults && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Fiyat senkronizasyonu başarıyla tamamlandı!
              </Typography>
            </Alert>

            <Typography variant="h6" sx={{ mb: 2 }}>
              Senkronizasyon Sonuçları
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <TrendingUpIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Eklenen Fiyatlar"
                  secondary={`${formatNumber(syncResults.added || 0)} yeni fiyat`}
                />
                <Chip 
                  label="Yeni" 
                  color="success" 
                  size="small" 
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <SyncIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Güncellenen Fiyatlar"
                  secondary={`${formatNumber(syncResults.updated || 0)} fiyat güncellendi`}
                />
                <Chip 
                  label="Güncellendi" 
                  color="primary" 
                  size="small" 
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <TrendingDownIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary="Kaldırılan Fiyatlar"
                  secondary={`${formatNumber(syncResults.removed || 0)} fiyat kaldırıldı`}
                />
                <Chip 
                  label="Kaldırıldı" 
                  color="warning" 
                  size="small" 
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                Toplam: {formatNumber((syncResults.added || 0) + (syncResults.updated || 0))} fiyat işlendi
              </Typography>
            </Box>
          </Box>
        )}

        {syncStatus === 'error' && (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Fiyat senkronizasyonu sırasında hata oluştu.
              </Typography>
            </Alert>

            {error && (
              <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="error.main">
                  <strong>Hata Detayı:</strong> {error}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              Lütfen daha sonra tekrar deneyin veya sistem yöneticisi ile iletişime geçin.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {syncStatus === 'idle' && (
          <>
            <Button onClick={onClose}>
              İptal
            </Button>
            <Button
              onClick={handleSync}
              variant="contained"
              startIcon={<SyncIcon />}
              disabled={isLoading}
            >
              Senkronizasyonu Başlat
            </Button>
          </>
        )}

        {syncStatus === 'syncing' && (
          <Button disabled>
            Senkronize Ediliyor...
          </Button>
        )}

        {(syncStatus === 'success' || syncStatus === 'error') && (
          <>
            {syncStatus === 'error' && (
              <Button
                onClick={handleSync}
                startIcon={<SyncIcon />}
                disabled={isLoading}
              >
                Tekrar Dene
              </Button>
            )}
            <Button onClick={onClose} variant="contained">
              Kapat
            </Button>
          </>
        )}
      </DialogActions>

      <style jsx>{`
        .rotate-animation {
          animation: rotate 2s linear infinite;
        }
        
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Dialog>
  )
}

export default PriceSyncDialog